import React from "react";
import { Box, Button, useMediaQuery, useTheme } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { Menu } from "lucide-react";

import Topbar from "../components/topbar";
import { useListSearch, ListTitle } from "../components/ui/list-search";
import DeletePopup from "../components/popup/delete";
import DeletePopupMobile from "../components/popup/mobile/delete-mobile";
import HardDeleteModal from "../components/popup/hard-delete-modal";
import ImportSummaryPopup from "../components/popup/import-summary";
import clientImage from '../files/images/staff.png';
import DynamicTableVirtual from "../components/table-virtual";
import SidebarMobileDrawer from "../components/sidebar-mobile-drawer";
import { useSidebarMobile } from "../providers/sidebar-mobile-context";

import useApi from "../hooks/use-api";
import buildApiRequestConfig from "../api/url-builder";
import routes from "../api/routes";
import { useUser } from "../providers/user-provider";
import { usePermissions } from "../providers/permissions-provider";
import { useToast } from "../providers/toaster-provider";
import { useSettings } from "../providers/settings-provider";

type ImportsTableRow = {
    id: number;
    moduleName: string;
    fileName: string;
    success: boolean;
    createdCount: number;
    totalProcessed: number;
    message: string;
    createdAt: string;
    updatedAt: string;
    transactionSummary?: {
        total: number;
        active: number;
        inactive: number;
    };
}

const Imports: React.FC = () => {
    const { user } = useUser();
    const { can } = usePermissions();
    const canDelete = can('imports', 'delete');
    const { showError, showSuccess } = useToast();
    const [searchParams] = useSearchParams();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const DeleteModal = isMobile ? DeletePopupMobile : DeletePopup;
    const { sidebarOpen, setSidebarOpen } = useSidebarMobile();
    const [openModal, setOpenModal] = React.useState<string>('');
    const [selectedImport, setSelectedImport] = React.useState<ImportsTableRow | null>(null);
    const [selectedImportId, setSelectedImportId] = React.useState<string | null>(null);
    const [data, setData] = React.useState<ImportsTableRow[]>([]);
    const { formatDate } = useSettings();
    // Search exactly the columns the table shows, as they're shown - the
    // padded ID, the formatted date & time, module, file name, and the
    // status label - not the raw row, whose hidden fields (result message,
    // ISO timestamps, counts) matched terms nobody could see on screen.
    const getSearchText = React.useCallback((row: ImportsTableRow) => [
        row.id,
        row.createdAt ? formatDate(new Date(row.createdAt), true) : null,
        row.moduleName,
        row.fileName,
        row.success ? 'Success' : 'Incomplete',
    ], [formatDate]);
    const list = useListSearch(data, getSearchText);
    const [hardDeleteNotes, setHardDeleteNotes] = React.useState<string>('');

    const [fetchImports, setFetchImports] = useApi();
    const [deleteImport, setDeleteImport] = useApi();
    const [hardDeleteImport, setHardDeleteImport] = useApi();

    React.useEffect(() => {
        document.title = "FineAnts | Imports";
        if (user?.entityId) {
            const { url, method } = buildApiRequestConfig(routes.imports.fetchByEntity, [{ param: 'entityId', value: user.entityId }]);
            setFetchImports({ url, method });
        }
    }, [user, deleteImport]);

    // Handle importId query parameter to open summary
    React.useEffect(() => {
        const importId = searchParams.get('importId');
        if (importId && data.length > 0) {
            setSelectedImportId(importId);
            setOpenModal('summary');
        }
    }, [searchParams, data]);

    React.useEffect(() => {
        const { data: importsData, isLoading, error } = fetchImports;
        if (importsData && !isLoading) {
            // Transform API data to table format
            const transformedData: ImportsTableRow[] = importsData.map((importItem: any, index: number) => ({
                id: String(importItem.id).padStart(3, '0'),
                moduleName: importItem.moduleName,
                fileName: importItem.fileName,
                description: importItem.description,
                success: importItem.result?.success || false,
                createdCount: importItem.result?.createdCount || 0,
                totalProcessed: importItem.result?.totalProcessed || 0,
                message: importItem.result?.message || '',
                createdAt: importItem.createdAt,
                updatedAt: importItem.updatedAt,
                transactionSummary: importItem.transactionSummary,
            }));
            setData(transformedData);
        }
        if (error) {
            showError("Failed to fetch imports");
        }
    }, [fetchImports, showError, setData]);

    React.useEffect(() => {
        const { data, isLoading, error } = deleteImport;
        if (data && !isLoading) {
            showSuccess("Import deleted successfully");
            setOpenModal('');
        }
        if (error) {
            showError(typeof error === 'string' ? error : "Failed to delete import");
        }
    }, [deleteImport, showError, showSuccess, user, setFetchImports]);

    React.useEffect(() => {
        const { data, isLoading, error } = hardDeleteImport;
        if (data && !isLoading) {
            showSuccess("Hard delete request sent.");
            setOpenModal('');
        }
        if (error) {
            showError(typeof error === 'string' ? error : "Failed to send hard delete request");
        }
    }, [hardDeleteImport, showError, showSuccess, user, setFetchImports]);

    const handleDelete = (row: Record<string, any>) => {
        setSelectedImport(row as ImportsTableRow);
        setOpenModal('delete');
    };

    const handleViewSummary = (row: Record<string, any>) => {
        setSelectedImportId(row.id);
        setOpenModal('summary');
    };

    const handleConfirmDelete = () => {
        if (selectedImport) {
            const isReverted = selectedImport.transactionSummary?.active === 0 && selectedImport.transactionSummary?.total > 0;
            const endpoint = isReverted ? routes.imports.restore : routes.imports.delete;
            const { url, method } = buildApiRequestConfig(endpoint, [{ param: 'id', value: selectedImport.id }]);
            setDeleteImport({ url, method });
        }
    };

    const handleHardDelete = (row: Record<string, any>) => {
        setSelectedImport(row as ImportsTableRow);
        setHardDeleteNotes('');
        setOpenModal('hardDelete');
    };

    const handleConfirmHardDelete = () => {
        if (selectedImport) {
            const { url, method } = buildApiRequestConfig(routes.imports.requestHardDelete, [{ param: 'id', value: selectedImport.id }]);
            setHardDeleteImport({ 
                url, 
                method,
                body: { notes: hardDeleteNotes } 
            });
        }
    };

    const columns = [
        { id: 'id', label: 'ID', width: 75, type: 'string' as const },
        {
            id: 'createdAt',
            label: 'Date & Time',
            width: 150,
            type: 'date' as const,
            showTime: true,
            mobilePrimary: true,
            mobileOrder: 3,
            mobileWidth: 100,
            // Date and time on two lines - on one line the time was cut off
            // mid-character in the card's narrow column.
            mobileElement: (value: any) => {
                if (!value) return '-';
                const d = new Date(value);
                const date = formatDate(d);
                const time = formatDate(d, true).replace(date, '').trim();
                return (
                    <Box sx={{ lineHeight: 1.25 }}>
                        <div>{date}</div>
                        {time && <Box component="div" sx={{ fontSize: 11, fontWeight: 500, color: 'var(--color-text-muted)' }}>{time}</Box>}
                    </Box>
                );
            },
        },
        { id: 'moduleName', label: 'Module', width: 120, type: 'string' as const },
        {
            id: 'fileName',
            label: 'File Name',
            width: 150,
            type: 'string' as const,
            mobilePrimary: true,
            mobileOrder: 1,
            mobileWidth: 120,
            mobileHideLabel: true,
        },
        {
            id: 'status',
            label: 'Status',
            width: 150,
            type: 'custom' as const,
            mobilePrimary: true,
            mobileOrder: 2,
            mobileWidth: 80,
            mobileHideLabel: true,
            customElement: (value: any, row: Record<string, any>) => (
                <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
                    {row.success ? (
                        <Box sx={{ color: 'var(--color-primary)', fontWeight: 600 }}>Success</Box>
                    ) : (
                        <Box sx={{ color: '#FF3B30', fontWeight: 600 }}>Incomplete</Box>
                    )}
                </Box>
            )
        },
        { 
            id: 'summary', 
            label: 'Summary', 
            width: 200, 
            type: 'custom' as const,
            customElement: (value: any, row: Record<string, any>) => (
                <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleViewSummary(row)}
                        sx={{ 
                            textTransform: 'none',
                            backgroundColor: 'var(--color-page-background)',
                            minWidth: 'auto',
                            px: 1,
                            py: 0.5,
                            fontSize: '0.75rem'
                        }}
                    >
                        View Details
                    </Button>
                </Box>
            )
        },
        { 
            id: 'actions', 
            label: 'Actions', 
            size: 50, 
            type: 'actions' as const
        },
    ];

    return (
        <div style={{ width: '100%' }}>
            <DeleteModal
                open={openModal === 'delete'}
                message={selectedImport?.transactionSummary?.active === 0 && selectedImport?.transactionSummary?.total > 0 ? "Restore Import?" : "Revert Import?"}
                description={selectedImport?.transactionSummary?.active === 0 && selectedImport?.transactionSummary?.total > 0 ? "This action will restore all data associated with this import." : "This action will erase all data associated with this import."}
                image={{ src: clientImage, width: 150, height: 150 }}
                onCancel={() => {
                    setOpenModal('');
                    setSelectedImport(null);
                }}
                onConfirm={handleConfirmDelete}
            />
            <HardDeleteModal
                open={openModal === 'hardDelete'}
                message="Request Hard Delete?"
                description="This action will completely erase all data associated with this import from the system once approved."
                image={{ src: clientImage, width: 150, height: 150 }}
                confirmLabel="Send Request"
                notesLabel="Notes (Optional)"
                notesPlaceholder="Please explain why this import needs to be completely removed..."
                notesRequired={false}
                notes={hardDeleteNotes}
                onNotesChange={setHardDeleteNotes}
                onCancel={() => {
                    setOpenModal('');
                    setSelectedImport(null);
                    setHardDeleteNotes('');
                }}
                onConfirm={handleConfirmHardDelete}
            />
            <ImportSummaryPopup
                open={openModal === 'summary'}
                onClose={() => {
                    setOpenModal('');
                    setSelectedImportId(null);
                }}
                importId={selectedImportId}
            />
            <Topbar
                title={<ListTitle title="Imports" list={list} />}
                isMobile={isMobile}
                mobileMenuButton={
                    isMobile && (
                        <Button 
                            variant="contained" 
                            color="primary" 
                            onClick={() => setSidebarOpen(true)} 
                            sx={{ 
                                color: '#fff', 
                                height: 40,
                                display: 'flex',
                                gap: 1,
                                minWidth: 40,
                                p: 1,
                                backgroundColor: 'var(--color-primary)', 
                                '&:hover': { backgroundColor: 'var(--color-primary)' } 
                            }}
                        >
                            <Menu size={20} color="#fff"/>
                        </Button>
                    )
                }
            >
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: '100%', justifyContent: 'flex-end' }} />
            </Topbar>
            <div style={{ padding: '10px' }}>
                <DynamicTableVirtual
                    columns={columns}
                    data={list.filtered}
                    isLoading={fetchImports.isLoading}
                    scrollMode="page"
                    additionalActions={[
                        {
                            label: (row: Record<string, any>) => (row.transactionSummary?.total > 0 && row.transactionSummary?.active === 0) ? 'Restore Import' : 'Revert Import',
                            onClick: handleDelete,
                            icon: (row: Record<string, any>) => (row.transactionSummary?.total > 0 && row.transactionSummary?.active === 0) ? 'check' : 'delete',
                            disabled: !canDelete,
                        },
                        {
                            label: 'Request Hard Delete',
                            onClick: handleHardDelete,
                            icon: 'warning',
                            visible: (row: Record<string, any>) => row.transactionSummary?.total > 0 && row.transactionSummary?.active === 0,
                            disabled: !canDelete,
                        }
                    ]}
                />
            </div>
            <SidebarMobileDrawer 
                open={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
            />
        </div>
    );
};

export default Imports;